import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { EnvelopeSimple, Lock, MapPin } from '@phosphor-icons/react';

function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).filter(Boolean).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/explore');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center px-4 py-12 bg-[#F9F9F6]" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-[#FFD166] border-2 border-[#004E89] flex items-center justify-center mx-auto mb-4 shadow-[4px_4px_0px_#004E89]">
            <MapPin size={32} weight="fill" color="#004E89" />
          </div>
          <h1 className="text-3xl font-black text-[#004E89] mb-2" style={{ fontFamily: 'Outfit' }}>Welcome Back</h1>
          <p className="text-[#5C7A99] text-sm">Sign in to find sales near you</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white border-2 border-[#004E89] rounded-2xl p-8 shadow-[4px_4px_0px_#004E89]" data-testid="login-form">
          {error && (
            <div className="bg-red-50 border-2 border-red-400 rounded-xl p-3 mb-4 text-red-700 text-sm font-medium" data-testid="login-error">
              {error}
            </div>
          )}

          <div className="mb-4">
            <label className="block text-sm font-bold text-[#004E89] mb-2 uppercase tracking-wide" style={{ fontFamily: 'Outfit' }}>Email</label>
            <div className="relative">
              <EnvelopeSimple size={20} weight="bold" className="absolute left-4 top-1/2 -translate-y-1/2 text-[#5C7A99]" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-neo pl-12"
                placeholder="you@example.com"
                required
                data-testid="login-email-input"
              />
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-bold text-[#004E89] mb-2 uppercase tracking-wide" style={{ fontFamily: 'Outfit' }}>Password</label>
            <div className="relative">
              <Lock size={20} weight="bold" className="absolute left-4 top-1/2 -translate-y-1/2 text-[#5C7A99]" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-neo pl-12"
                placeholder="Your password"
                required
                data-testid="login-password-input"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full py-3 text-lg"
            data-testid="login-submit-button"
          >
            {loading ? 'Signing In...' : 'Sign In'}
          </button>

          <p className="text-center text-sm text-[#5C7A99] mt-6">
            Don't have an account?{' '}
            <Link to="/register" className="text-[#FF6B35] font-bold hover:underline" data-testid="login-to-register-link">
              Sign Up
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
